/**
 * Format a UTC timestamp to Pakistan Time (PKT, UTC+5)
 * @param {string|Date} timestamp - UTC timestamp to format
 * @param {string} format - Format type: 'date', 'time', or 'datetime' (default: 'datetime')
 * @returns {string} Formatted date/time in Pakistan timezone
 */
export function formatToPakistanTime(timestamp, format = 'datetime') {
  if (!timestamp) return 'N/A';

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'N/A';

    if (format === 'date') {
      return date.toLocaleDateString('en-US', {
        timeZone: 'Asia/Karachi',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } else if (format === 'time') {
      return date.toLocaleTimeString('en-US', {
        timeZone: 'Asia/Karachi',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleString('en-US', {
        timeZone: 'Asia/Karachi',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    }
  } catch (err) {
    console.error('Error formatting date:', err);
    return 'Invalid Date';
  }
}

/**
 * Get today's date in Pakistan time
 * @returns {Date} Today's date in Pakistan timezone
 */
export function getTodayInPakistanTime() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Karachi',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return new Date(`${get('year')}-${get('month')}-${get('day')}T00:00:00`);
}

/**
 * Format timestamp with abbreviated format (e.g., "5 Jan, 2026 at 10:30 AM")
 * @param {string|Date} timestamp - UTC timestamp to format
 * @returns {string} Abbreviated formatted date/time
 */
export function formatAbbreviated(timestamp) {
  if (!timestamp) return 'N/A';

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'N/A';

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Karachi',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).formatToParts(date);

    const get = (type) => parts.find((p) => p.type === type)?.value || '';
    return `${get('day')} ${get('month')}, ${get('year')} at ${get('hour')}:${get('minute')} ${get('dayPeriod')}`;
  } catch (err) {
    console.error('Error formatting date:', err);
    return 'Invalid Date';
  }
}

/**
 * Get date range in Pakistan time for filtering
 * @param {string} rangeType - 'today', 'week', 'month', or 'all'
 * @returns {object} Object with startDate and endDate
 */
export function getDateRangeInPakistanTime(rangeType = 'today') {
  const today = getTodayInPakistanTime();
  
  let startDate, endDate;
  
  switch (rangeType) {
    case 'today':
      startDate = new Date(today);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
      
    case 'week':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - today.getDay());
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
      
    case 'month':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
      
    case 'all':
    default:
      return { startDate: null, endDate: null };
  }
  
  return { startDate, endDate };
}

/**
 * Format a date as YYYY-MM-DD for input fields (in Pakistan time)
 * @param {string|Date} timestamp - UTC timestamp
 * @returns {string} Date in YYYY-MM-DD format
 */
export function formatForInput(timestamp) {
  if (!timestamp) return '';

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Karachi',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch (err) {
    console.error('Error formatting date:', err);
    return '';
  }
}
